import type { Reading } from '@casa/driver-sdk';
import type { TileOf } from '@casa/model';
import { hexToRgb, hsvToHex, kelvinToHex } from '@casa/util';
import type { IconDefinition } from '../../elements/icon.ts';
import { iconElement, type LayoutElement } from '../../elements/layout_elements.ts';

/**
 * The `light` tile (docs/specs/light-tile.md, issue #36; the legacy `LightTile`): its icon in the
 * light's colour while it is on, grey while it is off or not known. Where it can do more than switch,
 * a chip under the icon says its level (or on or off) in a tint of its colour. A tap switches it; a
 * tap on the chip, or a hold, opens a card for its level, white tone and colour, where it has them.
 * Every change shows at once and goes to its source's driver.
 *
 * The legacy tile sent a toggle, which the driver turned into on or off from what it last knew;
 * this one sends the state the tile shows, so that two quick taps cannot cancel each other.
 */

export type LightTile = TileOf<'light'>;

/** `Yellow` and `Grey`, as the legacy view had them: a light without colours is yellow when on. */
export const LIGHT_ON_COLOR = '#ffff00';
export const LIGHT_OFF_COLOR = '#808080';

/** White tones offered where the driver says none (LT-11), in kelvin. */
export const WHITE_RANGE = { min: 2200, max: 6500 } as const;

/** What a light is and can do, from its source's last reading (LT-1, LT-2). */
export interface LightState {
    on: boolean | null;
    /** 0–100, where it dims. */
    level: number | undefined;
    /** `#rrggbb`, where it has colours. */
    color: string | undefined;
    /** Kelvin, where it has white tones. */
    colorTemperature: number | undefined;
    /** Which of the two it shows, where it says. */
    mode: 'CT' | 'RGB' | undefined;
    canDim: boolean;
    canWhite: boolean;
    canColor: boolean;
}

const isRecord = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const numberOf = (value: unknown) : number | undefined => {
    const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;

    return typeof number === 'number' && Number.isFinite(number) ? number : undefined;
};

function onOf(value: unknown) : boolean | null {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value === 'on' || value === 'off') {
        return value === 'on';
    }

    return null;
}

/**
 * The colour of a record: its `color` where that is `#rrggbb`; else from `hue` and `saturation`,
 * each 0–100, as Hubitat reports them (LT-1).
 */
function colorOf(record: Record<string, unknown>) : string | undefined {
    if (typeof record.color === 'string' && hexToRgb(record.color)) {
        return record.color.toLowerCase();
    }
    const hue = numberOf(record.hue);
    const saturation = numberOf(record.saturation);

    return hue !== undefined && saturation !== undefined ? hsvToHex({ h: hue * 3.6, s: saturation / 100, v: 1 }) : undefined;
}

export function lightState(readings: Reading[] | null | undefined) : LightState {
    const value = readings?.at(-1)?.value;
    if (isRecord(value)) {
        const level = numberOf(value.level);
        const colorTemperature = numberOf(value.colorTemperature);
        const color = colorOf(value);
        const mode = typeof value.colorMode === 'string' ? value.colorMode.toUpperCase() : undefined;

        return {
            on: onOf(value.switch),
            level,
            color,
            colorTemperature,
            mode: mode === 'CT' || mode === 'RGB' ? mode : undefined,
            canDim: level !== undefined,
            canWhite: colorTemperature !== undefined,
            canColor: color !== undefined,
        };
    }
    // A source of a level alone (Hue's `brightness`): on above 0.
    const level = typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    if (level !== undefined) {
        return { on: level > 0, level, color: undefined, colorTemperature: undefined, mode: undefined, canDim: true, canWhite: false, canColor: false };
    }

    return { on: onOf(value), level: undefined, color: undefined, colorTemperature: undefined, mode: undefined, canDim: false, canWhite: false, canColor: false };
}

/** Whether the light can do more than switch: then a hold and its chip open its card (LT-9, LT-10). */
export const hasControls = (state: LightState) : boolean => state.canDim || state.canWhite || state.canColor;

/** The icon's colour while on (LT-6): its colour, or the white of its tone, or yellow. */
export function lightColor(state: LightState) : string {
    const white = state.colorTemperature !== undefined ? kelvinToHex(state.colorTemperature) : undefined;
    if (state.mode === 'RGB' && state.color) {
        return state.color;
    }
    if (state.mode === 'CT' && white) {
        return white;
    }

    return state.color ?? white ?? LIGHT_ON_COLOR;
}

/** The chip under the icon (LT-6, LT-10): what it says, and the light's colour to tint it with while on. */
export interface LevelChip {
    readonly text: string;
    readonly tint: string | undefined;
}

/**
 * The chip, for a light that can do more than switch and whose state is known: its level while on
 * ("On" where it does not dim), "Off" while off; a tap on it opens the card.
 */
export function levelChipOf(state: LightState) : LevelChip | undefined {
    if (!hasControls(state) || state.on === null) {
        return undefined;
    }
    if (!state.on) {
        return { text: 'Off', tint: undefined };
    }

    return { text: state.canDim && state.level !== undefined ? `${Math.round(state.level)}%` : 'On', tint: lightColor(state) };
}

/** Where the chip goes, in parts of the content area: under the icon, centred (the view places it). */
export const CHIP_BOX = { top: 0.72, height: 0.18 } as const;

/** The icon: the whole area, or above the chip where there is one (LT-7). */
export function lightTileElements(state: LightState, icon: IconDefinition | undefined) : LayoutElement[] {
    const color = state.on === true ? lightColor(state) : LIGHT_OFF_COLOR;
    if (!levelChipOf(state)) {
        return [iconElement({ center: 'parent.center', w: 'parent.width*0.8', h: 'parent.height*0.8' }, icon, color, { fill: true })];
    }

    return [iconElement({ centerX: 'parent.center.X', top: 'parent.top + parent.height*0.04', w: 'parent.width*0.8', h: 'parent.height*0.62' }, icon, color, { fill: true })];
}

/** A change the card makes (LT-11). */
export type LightChange = { on: boolean } | { level: number } | { colorTemperature: number } | { color: string };

/** The command for a change, the same for every driver (LT-4). */
export function lightCommand(change: LightChange) : { command: string; argument?: unknown } {
    if ('on' in change) {
        return { command: change.on ? 'on' : 'off' };
    }
    if ('level' in change) {
        return { command: 'setLevel', argument: Math.round(change.level) };
    }
    if ('colorTemperature' in change) {
        return { command: 'setColorTemperature', argument: Math.round(change.colorTemperature) };
    }

    return { command: 'setColor', argument: change.color };
}

/**
 * The readings to show at once after a change, in the reading's own form: a record changed (a level
 * or a colour switches the light on, LT-12), or a level or on/off of their own.
 */
export function changedReadings(readings: Reading[] | null | undefined, change: LightChange) : Reading[] {
    const last = readings?.at(-1);
    const value = last?.value;
    let changed: unknown;
    if (isRecord(value)) {
        changed = 'on' in change
            ? { ...value, switch: change.on ? 'on' : 'off' }
            : 'level' in change
              ? { ...value, level: Math.round(change.level), switch: change.level > 0 ? 'on' : 'off' }
              : 'colorTemperature' in change
                ? { ...value, colorTemperature: Math.round(change.colorTemperature), colorMode: 'CT', switch: 'on' }
                : { ...value, color: change.color, colorMode: 'RGB', switch: 'on' };
    } else if (typeof value === 'number') {
        changed = 'on' in change ? (change.on ? (value > 0 ? value : 100) : 0) : 'level' in change ? Math.round(change.level) : value;
    } else if (typeof value === 'boolean') {
        changed = 'on' in change ? change.on : value;
    } else {
        changed = 'on' in change ? (change.on ? 'on' : 'off') : value;
    }

    return [{ ...last, value: changed }];
}

/** The card's ready colours (LT-11): two whites (as tones where the light has them), then six colours. */
export const SWATCHES: readonly { label: string; color: string; kelvin?: number }[] = [
    { label: 'Warm white', color: kelvinToHex(2700), kelvin: 2700 },
    { label: 'Cool white', color: kelvinToHex(5500), kelvin: 5500 },
    { label: 'Red', color: '#ff3b30' },
    { label: 'Orange', color: '#ff9500' },
    { label: 'Yellow', color: '#ffd60a' },
    { label: 'Green', color: '#34c759' },
    { label: 'Blue', color: '#0a84ff' },
    { label: 'Purple', color: '#bf5af2' },
];

/** The change a swatch makes: a white tone where the light has them, else its colour. */
export const swatchChange = (swatch: (typeof SWATCHES)[number], state: LightState) : LightChange =>
    swatch.kelvin !== undefined && state.canWhite ? { colorTemperature: swatch.kelvin } : { color: swatch.color };
